import { useEffect, useMemo, useState } from "react";

import {
  isDefaultLanguage,
  isSupportedLanguage,
  LANGUAGE_CONFIG,
} from "../../../config";
import { detectLanguage, translateText } from "../../../services/api/core";

/** @type {Map<string, string>} */
const detectCache = new Map();
/** @type {Map<string, string>} */
const translateCache = new Map();

/**
 * Collect unique non-empty work descriptions from payout day rows.
 * Each shift still shows its own hover; this list is only so detect (and
 * a later translate, when the source is not the default language) runs once per distinct string, not once
 * per duplicate note on the week. This saves API calls for repeated work descriptions.
 *
 * @param {Array<Object>|null|undefined} shiftsToRender Day rows with nested shifts.
 * @returns {string[]} Trimmed descriptions, first-seen order.
 */
function uniqueDescriptions(shiftsToRender) {
  const seen = new Set();
  const texts = [];
  (shiftsToRender || []).forEach((day) => {
    (day.shifts || []).forEach((shift) => {
      const text = (shift.workDescription || "").trim();
      if (text && !seen.has(text)) {
        seen.add(text);
        texts.push(text);
      }
    });
  });
  return texts;
}

/**
 * Detect languages for unique payout notes, then translate non-default ones
 * into the default language. Hover can show the original immediately; the
 * translation replaces it when ready.
 * Never writes translations back into `workDescription`.
 *
 * @param {Array<Object>|null|undefined} shiftsToRender Day rows with nested shifts.
 * @returns {Object<string, string>} Map of original text → default-language hover text.
 */
export function usePayoutWorkDescriptionHover(shiftsToRender) {
  const textsKey = useMemo(
    () => uniqueDescriptions(shiftsToRender).join("\0"),
    [shiftsToRender]
  );
  const [translatedByText, setTranslatedByText] = useState({});

  useEffect(() => {
    const texts = textsKey ? textsKey.split("\0") : [];
    if (!texts.length) {
      setTranslatedByText({});
      return undefined;
    }

    let cancelled = false;

    /**
     * Detect uncached notes, then translate those in a non-default supported language.
     *
     * @returns {Promise<void>}
     */
    const loadHoverTranslations = async () => {
      const uncached = texts.filter((text) => !detectCache.has(text));
      if (uncached.length) {
        try {
          const result = await detectLanguage(uncached);
          if (result?.success && Array.isArray(result.languages)) {
            uncached.forEach((text, index) => {
              detectCache.set(text, result.languages[index] || "");
            });
          }
        } catch (error) {
          console.error("Error detecting payout work-description language:", error);
        }
      }
      if (cancelled) {
        return;
      }

      const needsTranslation = texts.filter((text) => {
        const detected = detectCache.get(text);
        return (
          detected &&
          !isDefaultLanguage(detected) &&
          isSupportedLanguage(detected)
        );
      });
      const toTranslate = needsTranslation.filter((text) => !translateCache.has(text));
      await Promise.all(
        toTranslate.map(async (text) => {
          try {
            const result = await translateText(
              text,
              detectCache.get(text),
              LANGUAGE_CONFIG.DEFAULT
            );
            if (result?.success) {
              translateCache.set(text, result.translatedText || "");
            }
          } catch (error) {
            console.error("Error translating payout work description:", error);
          }
        })
      );
      if (cancelled) {
        return;
      }

      const next = {};
      needsTranslation.forEach((text) => {
        if (translateCache.has(text)) {
          next[text] = translateCache.get(text);
        }
      });
      setTranslatedByText(next);
    };

    loadHoverTranslations();
    return () => {
      cancelled = true;
    };
  }, [textsKey]);

  return translatedByText;
}
